import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useDebouncedCallback } from "core/hooks/useDebouncedCallback";
import { useLocale } from "core/hooks/useLocale";
import type { useMenuItems } from "core/hooks/useMenuItems";
import { usePreloadDmsNode } from "features/DMS/hooks/usePreloadDmsNode";
import { NotificationDot } from "features/Notifications/NotificationDot";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomLink } from "../CustomLink";

export interface LibraryLinksProps {
  libraries: ReturnType<typeof useMenuItems>["menuItems"][number]["libraries"];
  numberOfInitiallyVisibleLinks: number;
}

function MenuLibraryToolTip({
  path,
  onPointerOver,
  name,
  id,
}: {
  path: string;
  onPointerOver?: (path: string) => void;
  name: string;
  id: string;
}) {
  const locale = useLocale();
  const [isHovered, setIsHovered] = useState(false);

  const { data } = useCentrixApi({
    path: "/api/app/doc-node/library-summary",
    parameters: { query: { locale, path, isEdit: false } },
    shouldFetch: isHovered,
  });
  return (
    <Tooltip title={data?.description ?? ""} placement="right" arrow>
      <ListItem
        disableGutters
        onPointerOver={() => {
          setIsHovered(true);
          onPointerOver?.(path);
        }}
      >
        <CustomLink
          href={`/dms/library/v2${path}`}
          underline="none"
          color="primary.dark"
        >
          {name}
          <NotificationDot mode="parent" id={id!} />
        </CustomLink>
      </ListItem>
    </Tooltip>
  );
}

export const LibraryLinks = memo(
  ({ libraries, numberOfInitiallyVisibleLinks }: LibraryLinksProps) => {
    const { t } = useTranslation("Fixhub");
    const preloadNode = usePreloadDmsNode();
    const preloadDebounced = useDebouncedCallback(
      preloadNode,
      [preloadNode],
      200
    );
    const onPointerOver = useCallback(
      (path: string) => {
        preloadDebounced(path, "Library", "view");
      },
      [preloadDebounced]
    );

    const [isExpanded, setIsExpanded] = useState(false);
    const visibleLibraries = isExpanded
      ? libraries
      : libraries.slice(0, numberOfInitiallyVisibleLinks);
    const isShowAllButtonVisible =
      !isExpanded && libraries.length > numberOfInitiallyVisibleLinks;

    return (
      <List sx={{ width: "24ch" }}>
        <ListItem disableGutters>
          <Typography variant="button" textAlign="center">
            {t("Next:Core:LibraryLinks.title")}
          </Typography>
        </ListItem>
        <Divider />
        {visibleLibraries.map(({ name, id, path }) => (
          <MenuLibraryToolTip
            key={path}
            path={path!}
            name={name!}
            id={id!}
            onPointerOver={onPointerOver}
          />
        ))}
        <Collapse in={isShowAllButtonVisible}>
          <ListItem
            disableGutters
            sx={{
              p: 0,
              my: 1,
              borderBlockStart: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <ListItemButton
              onClick={() => setIsExpanded(true)}
              sx={{ justifyContent: "center" }}
            >
              <KeyboardDoubleArrowDownIcon color="primary" />
            </ListItemButton>
          </ListItem>
        </Collapse>
      </List>
    );
  }
);
LibraryLinks.displayName = "LibraryLinks";
